import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiHttpError } from "../../lib/api";
import { holdCountdown, useCountdown } from "../../lib/useCountdown";

/**
 * `/claim/:code` — where a scanned party badge lands.
 *
 * THIS ROUTE WAS DELETED ONCE, and it is back narrower than it was. It used to be the
 * primary pairing path, produced by a QR on every waiting screen, and it went because
 * scanning happens in the phone's CAMERA APP: the fast path led out of the app and
 * finished in a browser tab nobody chose. That argument still holds for setting a screen
 * up, which is why typing is still what every surface explains. What it did not cover is a
 * party — a queue of strangers, no operator to hand a code to — and that is the only thing
 * that produces this URL now. See PartyBadge.
 *
 * IT DOES NOT ACT ON GET. Link previewers (iMessage, Slack, WhatsApp) fetch URLs that get
 * shared, and a claim that happened during a preview would hand somebody's television to a
 * crawler. The page renders and POSTs from JS, which previewers do not run — the same
 * shape AdoptControlScreen uses.
 *
 * IT NAVIGATES RATHER THAN RELOADING, and that is the one place it deliberately differs
 * from AdoptControlScreen. That screen does a hard `window.location.replace` because it
 * CHANGES THIS BROWSER'S IDENTITY, and identity is read at module load in several places.
 * A claim changes nothing about who this browser is — it moves a device row to the
 * identity this tab already has — so a client-side navigate is correct and keeps the scan
 * fast, which for somebody standing in front of a television is the whole product.
 */
export function ClaimScreen() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  /** A local deadline while somebody else holds the screen. See useCountdown. */
  const [heldUntil, setHeldUntil] = useState<number | null>(null);
  /*
   * Bumped to try again, and it is what the one-shot guard is keyed on.
   *
   * Resetting a bare `useRef(false)` would be the obvious way to retry and is the wrong
   * one: StrictMode double-invokes effects in dev, so a guard that can be un-set is a
   * guard that lets the second invocation through and claims twice.
   */
  const [attempt, setAttempt] = useState(0);
  const attempted = useRef(-1);

  useEffect(() => {
    if (!code || attempted.current === attempt) return;
    attempted.current = attempt;
    setError(null);
    void api.screens
      .claim(code)
      // Straight to the remote: this QR exists to put the controller in somebody's hand,
      // and landing them on a page they have to scroll would make them go find it.
      .then(() => navigate("/remote", { replace: true }))
      .catch((e: unknown) => {
        if (e instanceof ApiHttpError && e.status === 423 && e.retryAfterSec !== undefined) {
          // Converted to a local deadline ON RECEIPT, so a phone whose clock disagrees
          // with the server still counts the right number of seconds down.
          setHeldUntil(Date.now() + e.retryAfterSec * 1000);
          return;
        }
        setError(
          e instanceof ApiHttpError && e.status === 404
            ? "That code has expired — scan the screen again."
            : e instanceof Error
              ? e.message
              : String(e),
        );
      });
  }, [code, attempt, navigate]);

  const left = useCountdown(heldUntil);

  // At zero, try again on their behalf. The code is in the URL and the person is standing
  // in front of the television; a page that reads 0:00 and then does nothing is broken.
  useEffect(() => {
    if (heldUntil === null || left > 0) return;
    setHeldUntil(null);
    setAttempt((a) => a + 1);
  }, [heldUntil, left]);

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-3 px-6 text-center">
      {error ? (
        <>
          <span className="text-lg text-zinc-200">Couldn&rsquo;t take this screen</span>
          <span className="max-w-sm text-sm text-zinc-500">{error}</span>
        </>
      ) : heldUntil !== null ? (
        <>
          <span className="text-lg text-zinc-200">Someone else has this screen</span>
          {/* Amber, not red: this is a place in a queue, not something they did wrong. */}
          <span className="text-4xl font-semibold tabular-nums text-amber-300">
            {holdCountdown(left)}
          </span>
          <span className="max-w-sm text-sm text-zinc-500">
            You can claim it when this runs out. Keep this page open — it will take the
            screen for you.
          </span>
        </>
      ) : (
        <>
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-accent)]/25 border-t-[var(--color-accent)]" />
          <span className="text-sm text-zinc-500">Taking the screen…</span>
        </>
      )}
    </div>
  );
}
